// The signature pad of the consent step (design/screens/kiosk.html `.sigpad`; PAT-71; BR-GOVERNANCE-020): a finger or stylus
// draws on a canvas; the drawing leaves as a PNG carried inline in the capture (`{ contentType: 'image/png', data }`). The
// canvas is sized to its box at device-pixel-ratio so the stored image is crisp — but never past MAX_UPLOAD_PX on its longer
// side, which the server refuses as `too_many_pixels` — and the strokes are replayed after a resize so rotating the tablet
// does not lose a signature. A change is reported when a stroke starts and again when it ends, so the parent's `toPng()`
// snapshot holds the finished stroke, not its first touch. Clear empties it.
//
// The pad on screen draws in the page's text colour, so it reads in a dark theme; the stored image never does. `toPng()`
// replays the strokes onto a canvas of its own in fixed dark ink on white, sized by the same cap, so the POPIA evidence that
// goes on the consent PDF and the documents tab is legible wherever it is shown, whatever theme the tablet was in.
import { MAX_UPLOAD_PX } from "@visionx/contracts";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

export interface SignaturePadHandle {
  /** Standard base64 of the PNG, or null while nothing has been drawn. */
  toPng: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

type Point = { x: number; y: number };

/** The stored signature's ink and ground, independent of the page theme (the design's `--text` in the light theme). */
export const SIGNATURE_INK = "#16202a";
export const SIGNATURE_PAPER = "#ffffff";
const LINE_WIDTH = 2.2;

/**
 * The backing store for a box of `width` × `height` CSS pixels: canvas pixels per CSS pixel (the device pixel ratio, lowered so
 * the longer side stays within MAX_UPLOAD_PX) and the canvas size, each side between 1 and MAX_UPLOAD_PX.
 */
export function backingStore(width: number, height: number, devicePixelRatio: number): { scale: number; width: number; height: number } {
  const scale = Math.min(devicePixelRatio > 0 ? devicePixelRatio : 1, MAX_UPLOAD_PX / Math.max(width, height, 1));
  const side = (css: number) => Math.max(1, Math.min(MAX_UPLOAD_PX, Math.round(css * scale)));
  return { scale, width: side(width), height: side(height) };
}

function strokeAll(ctx: CanvasRenderingContext2D, strokes: readonly Point[][], scale: number, ink: string): void {
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(scale, scale);
  ctx.lineWidth = LINE_WIDTH;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.strokeStyle = ink;
  for (const stroke of strokes) {
    if (stroke.length === 0) continue;
    ctx.beginPath();
    ctx.moveTo(stroke[0]!.x, stroke[0]!.y);
    if (stroke.length === 1) ctx.lineTo(stroke[0]!.x + 0.1, stroke[0]!.y + 0.1);
    for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i]!.x, stroke[i]!.y);
    ctx.stroke();
  }
}

export const SignaturePad = forwardRef<SignaturePadHandle, { onChange?: ((empty: boolean) => void) | undefined; caption?: string | undefined; disabled?: boolean | undefined }>(function SignaturePad({ onChange, caption, disabled }, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<Point[][]>([]);
  const drawing = useRef(false);
  /** Canvas pixels per CSS pixel: the device pixel ratio, capped so the longer side stays within MAX_UPLOAD_PX. */
  const scale = useRef(1);
  const [empty, setEmpty] = useState(true);
  // The latest onChange, so the memoised `clear` (called by the parent after a version conflict) never reports through a stale closure.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // On screen only: the theme's text colour, so the strokes read on a dark pad too.
    strokeAll(ctx, strokes.current, scale.current, getComputedStyle(canvas).color || SIGNATURE_INK);
  }, []);

  const resize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const store = backingStore(rect.width, rect.height, window.devicePixelRatio || 1);
    scale.current = store.scale;
    if (canvas.width !== store.width || canvas.height !== store.height) { canvas.width = store.width; canvas.height = store.height; }
    paint();
  }, [paint]);

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [resize]);

  const point = (e: ReactPointerEvent<HTMLCanvasElement>): Point => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const mark = useCallback((isEmpty: boolean) => { setEmpty(isEmpty); onChangeRef.current?.(isEmpty); }, []);

  const onDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* a pointer the browser will not capture still draws */ }
    drawing.current = true;
    strokes.current.push([point(e)]);
    paint();
    mark(false);
  };
  const onMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    e.preventDefault();
    strokes.current[strokes.current.length - 1]?.push(point(e));
    paint();
  };
  // The stroke is finished: report again so the parent snapshots the whole drawing (the PNG is the POPIA consent evidence).
  const onUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChangeRef.current?.(strokes.current.length === 0);
  };

  const clear = useCallback(() => { strokes.current = []; paint(); mark(true); }, [paint, mark]);

  /** The stored image: the strokes replayed in SIGNATURE_INK on SIGNATURE_PAPER, on a canvas of their own within MAX_UPLOAD_PX. */
  const toPng = useCallback((): string | null => {
    const canvas = canvasRef.current;
    if (!canvas || strokes.current.length === 0) return null;
    const rect = canvas.getBoundingClientRect();
    // The pad's CSS box; if it has none right now (hidden mid-rotation), the size its backing store was last given.
    const width = rect.width > 0 ? rect.width : canvas.width / scale.current;
    const height = rect.height > 0 ? rect.height : canvas.height / scale.current;
    const store = backingStore(width, height, window.devicePixelRatio || 1);
    const out = document.createElement("canvas");
    out.width = store.width;
    out.height = store.height;
    const ctx = out.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = SIGNATURE_PAPER;
    ctx.fillRect(0, 0, store.width, store.height);
    strokeAll(ctx, strokes.current, store.scale, SIGNATURE_INK);
    const url = out.toDataURL("image/png");
    return url.slice(url.indexOf(",") + 1);
  }, []);

  useImperativeHandle(ref, () => ({
    toPng,
    clear,
    isEmpty: () => strokes.current.length === 0,
  }), [toPng, clear]);

  return (
    <div className="sigpad" data-empty={empty ? "true" : "false"}>
      <canvas ref={canvasRef} role="img" aria-label="Signature pad — sign with your finger" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={onUp} />
      <span>{empty ? "Sign here" : caption ?? "Signed"}</span>
      {!empty && <button type="button" className="linkbtn right" onClick={clear}>Clear</button>}
    </div>
  );
});
